import DataTable from 'src/components/dataTable';
import {useDeleteIntegrationConfig} from './mutations';
import {IntegrationConfig} from 'src/core/data/api/IntegrationConfig';
import {ViewType} from './container';
import {useIntl} from 'react-intl';
import useAlerts from 'src/hooks/use-alerts';
import useUsers from 'src/hooks/use-users';
import useTimezone from 'src/hooks/use-timezone';
import useStore from 'src/hooks/use-store';
import {formatDateTimeColumn} from 'src/helpers/excel-helper';
import {canSeeAuditFields} from 'src/helpers/auth';

interface IntegrationConfigListViewProps {
    integrationConfigs: IntegrationConfig[];
    loading: boolean;
    onViewChange: (viewType: ViewType, integration?: IntegrationConfig) => void;
}

const noCreator = {getUserById: () => undefined};

const IntegrationConfigListView = ({integrationConfigs, loading, onViewChange}: IntegrationConfigListViewProps) => {
    const intl = useIntl();
    const {toast} = useAlerts();
    const {users: userStore} = useUsers();
    const {timezone} = useTimezone();
    const {store} = useStore();
    const showAuditFields = canSeeAuditFields(store.auth.profile);
    const {mutate: deleteIntegrationConfig} = useDeleteIntegrationConfig();

    const list = integrationConfigs.map((item) => {
        const creator = (userStore ?? noCreator).getUserById(item.createdByUserId);
        return {
            ...item,
            creatorName: creator ? creator.name : '',
            creatorEmail: creator ? creator.email : '',
            createdAtFormatted: formatDateTimeColumn(item.createdAt, timezone),
            updatedAtFormatted: formatDateTimeColumn(item.updatedAt, timezone),
        };
    });

    const onAdd = () => {
        onViewChange('create');
    };

    const onEdit = (selectedIntegration: IntegrationConfig) => {
        onViewChange('edit', selectedIntegration);
    };

    const onApiError = (error: any) => {
        toast.errorStatus(error);
    };

    const onRemoveSuccess = () => {
        toast.success(intl.formatMessage({id: 'sharedRemovedSuccess'}));
        onViewChange('list');
    };

    const handleRemove = (selectedIntegration: IntegrationConfig) => {
        deleteIntegrationConfig(selectedIntegration.id, {
            onSuccess: onRemoveSuccess,
            onError: onApiError,
        });
    };

    return (
        <DataTable
            add={onAdd}
            edit={onEdit}
            remove={handleRemove}
            list={list}
            loading={loading}
            attributes={undefined}
            keyField={'id'}
            confirmText={intl.formatMessage({id: 'integrationConfigRemoveConfirm'})}
            headers={[
                {title: 'sharedUser', field: 'username', dataSort: true},
                {title: 'sharedDescription', field: 'description', dataSort: false},
                ...(showAuditFields ? [
                    {title: 'sharedCreatedBy', field: 'creatorName', dataSort: true},
                    {title: 'sharedCreatorEmail', field: 'creatorEmail', dataSort: true},
                    {title: 'sharedCreatedAt', field: 'createdAtFormatted', dataSort: true},
                    {title: 'sharedUpdatedAt', field: 'updatedAtFormatted', dataSort: true},
                ] : []),
            ]}
        />
    );
};

export default IntegrationConfigListView;
